interface RuntimeConfig {
  gatewayApiUrl: string;
  isometricApiUrl: string;
  isometricUrl: string;
  agentsApiUrl: string;
  n8nUrl: string;
  llmPlatform: string;
  keycloakUrl: string;
  realmName: string;
  clientId: string;
  microsoftLoginHint: string;
  // Deep-link URL for the Neo4j Browser (port 7474 by default). Optional —
  // when empty, the "Open in Neo4j Browser" affordance in the graph
  // visualizer is hidden. Self-hosted deployments set this to their own
  // Neo4j URL rather than inheriting a baked-in one.
  neo4jBrowserUrl: string;
  // Archive & retention config (BREEZEAI-1098). Values come through as strings
  // (JSON / env), parsed to numbers in the exported config object below.
  archiveRetentionDays: string;
  archiveUrgentDays: string;
  hardDeletePollMs: string;
  // Ignore-pattern validation limits for Git-backed code ontologies
  // (BREEZEAI-1238). Mirror the backend's IsIgnorePatternList rule; env values
  // override the defaults, which are applied in the exported config below.
  // `ignorePatternInvalidChars` is a plain list of forbidden characters
  // (default "<>"), not a regex — it is compiled into one in `config`.
  ignorePatternInvalidChars: string;
  ignorePatternMaxLineLength: string;
  ignorePatternsMaxTotalLength: string;
  // How long a code-ontology repository may sit in one in-flight ingest stage
  // before the list page offers a Retry (BREEZEAI-757). Milliseconds.
  // Keep this BELOW the backend's DIFF_ANALYSIS_STALE_THRESHOLD_MS (60 min by
  // default): the point is to let a user recover before the backend gives up
  // and rewrites the row as `error`.
  codeOntologyStuckAfterMs: string;
}

declare global {
  interface Window {
    // Present in production (written by docker-entrypoint.d/10-generate-config.sh).
    // Absent in local Vite dev mode — config.ts falls back to import.meta.env instead.
    __CONFIG__?: RuntimeConfig;
  }
}

// Fields that must be non-empty for the application to function.
const REQUIRED_KEYS = [
  "isometricApiUrl",
  "keycloakUrl",
  "realmName",
  "clientId",
] as const;

// Fallback defaults for the ignore-pattern limits. Used when the matching env
// var (VITE_* in dev, unprefixed on the container) is unset or unusable.
const DEFAULT_IGNORE_PATTERN_INVALID_CHARS = "<>";
const DEFAULT_IGNORE_PATTERN_MAX_LINE_LENGTH = 512;
const DEFAULT_IGNORE_PATTERNS_MAX_TOTAL_LENGTH = 10_000;
const DEFAULT_CODE_ONTOLOGY_STUCK_AFTER_MS = 15 * 60 * 1000;

/**
 * Parse a positive integer from a string config value, falling back to
 * `fallback` when the value is empty, non-numeric or not > 0.
 */
function toPositiveInt(raw: string | undefined, fallback: number): number {
  const n = Number.parseInt((raw ?? "").trim(), 10);
  return Number.isFinite(n) && n > 0 ? n : fallback;
}

/**
 * Compile a list of forbidden characters (e.g. "<>") into a character-class
 * regex. Returns null when the list is empty so callers can skip the check.
 */
function toCharClassRegex(chars: string): RegExp | null {
  if (!chars) return null;
  const escaped = chars.replace(/[\\\]^-]/g, "\\$&");
  return new RegExp(`[${escaped}]`);
}

function loadConfig(): RuntimeConfig {
  if (import.meta.env.DEV) {
    return {
      gatewayApiUrl:      import.meta.env.VITE_GATEWAY_API_URL      ?? "",
      isometricApiUrl:    import.meta.env.VITE_ISOMETRIC_API_URL    ?? "",
      isometricUrl:       import.meta.env.VITE_ISOMETRIC_URL        ?? "",
      agentsApiUrl:       import.meta.env.VITE_AGENTS_API_URL       ?? "",
      n8nUrl:             import.meta.env.VITE_N8N_URL              ?? "",
      llmPlatform:        import.meta.env.VITE_LLM_PLATFORM         ?? "",
      keycloakUrl:        import.meta.env.VITE_KEYCLOAK_URL         ?? "",
      realmName:          import.meta.env.VITE_REALM_NAME           ?? "",
      clientId:           import.meta.env.VITE_CLIENT_ID            ?? "",
      microsoftLoginHint:   import.meta.env.VITE_MICROSOFT_LOGIN_HINT    ?? "",
      neo4jBrowserUrl:      import.meta.env.VITE_NEO4J_BROWSER_URL      ?? "",
      archiveRetentionDays: import.meta.env.VITE_ARCHIVE_RETENTION_DAYS ?? "30",
      archiveUrgentDays:    import.meta.env.VITE_ARCHIVE_URGENT_DAYS    ?? "5",
      hardDeletePollMs:     import.meta.env.VITE_HARD_DELETE_POLL_MS    ?? "10000",
      ignorePatternInvalidChars:    import.meta.env.VITE_IGNORE_PATTERN_INVALID_CHARS     ?? DEFAULT_IGNORE_PATTERN_INVALID_CHARS,
      ignorePatternMaxLineLength:   import.meta.env.VITE_IGNORE_PATTERN_MAX_LINE_LENGTH   ?? String(DEFAULT_IGNORE_PATTERN_MAX_LINE_LENGTH),
      ignorePatternsMaxTotalLength: import.meta.env.VITE_IGNORE_PATTERNS_MAX_TOTAL_LENGTH ?? String(DEFAULT_IGNORE_PATTERNS_MAX_TOTAL_LENGTH),
      codeOntologyStuckAfterMs:     import.meta.env.VITE_CODE_ONTOLOGY_STUCK_AFTER_MS     ?? String(DEFAULT_CODE_ONTOLOGY_STUCK_AFTER_MS),
    };
  }

  // Production: window.__CONFIG__ must have been written by the container
  // entrypoint before nginx started serving.  Fail loudly here rather than
  // letting API calls fail later with opaque network errors.
  const rc = window.__CONFIG__;

  if (!rc) {
    throw new Error(
      "window.__CONFIG__ is not defined. " +
      "Ensure docker-entrypoint.d/10-generate-config.sh ran before nginx started " +
      "and that config.js is being served correctly."
    );
  }

  const missing = REQUIRED_KEYS.filter((k) => !rc[k]);
  if (missing.length > 0) {
    throw new Error(
      `Runtime config is missing required fields: ${missing.join(", ")}. ` +
      "Check that the corresponding environment variables are set on the container."
    );
  }

  return rc;
}

const _config = loadConfig();

export const config = {
  gatewayApiUrl:   _config.gatewayApiUrl,
  isometricApiUrl: _config.isometricApiUrl,
  isometricUrl:    _config.isometricUrl,
  agentsApiUrl:    _config.agentsApiUrl,
  n8nUrl:          _config.n8nUrl,
  llmPlatform:     _config.llmPlatform,
  neo4jBrowserUrl: _config.neo4jBrowserUrl,
  archiveRetentionDays: parseInt(_config.archiveRetentionDays || "30",    10),
  archiveUrgentDays:    parseInt(_config.archiveUrgentDays    || "5",     10),
  hardDeletePollMs:     parseInt(_config.hardDeletePollMs     || "10000", 10),
  // Ignore-pattern validation (BREEZEAI-1238). Env override or default.
  ignorePatternInvalidChars:      _config.ignorePatternInvalidChars || DEFAULT_IGNORE_PATTERN_INVALID_CHARS,
  ignorePatternInvalidCharsRegex: toCharClassRegex(_config.ignorePatternInvalidChars || DEFAULT_IGNORE_PATTERN_INVALID_CHARS),
  ignorePatternMaxLineLength:     toPositiveInt(_config.ignorePatternMaxLineLength,   DEFAULT_IGNORE_PATTERN_MAX_LINE_LENGTH),
  ignorePatternsMaxTotalLength:   toPositiveInt(_config.ignorePatternsMaxTotalLength, DEFAULT_IGNORE_PATTERNS_MAX_TOTAL_LENGTH),
  // Stuck-repo Retry threshold (BREEZEAI-757).
  codeOntologyStuckAfterMs:       toPositiveInt(_config.codeOntologyStuckAfterMs, DEFAULT_CODE_ONTOLOGY_STUCK_AFTER_MS),
};

export const oidcConfig = {
  authority:                   `${_config.keycloakUrl}/realms/${_config.realmName}`,
  client_id:                   _config.clientId,
  redirect_uri:                `${window.location.origin}/callback`,
  silent_redirect_uri:         `${window.location.origin}/silent-renew`,
  post_logout_redirect_uri:    `${window.location.origin}/`,
  response_type:               "code",
  automaticSilentRenew:        true,
  loadUserInfo:                true,
  monitorSession:              false,
  checkSessionInterval:        0,
  includeIdTokenInSilentRenew: true,
  extraQueryParams: {
    kc_idp_hint: _config.microsoftLoginHint,
  },
  onSigninCallback: () => {
    window.history.replaceState({}, document.title, window.location.pathname);
  },
};
